import { Component, ViewEncapsulation, OnInit, signal } from '@angular/core';
import { UserService } from '../../../../../shared/services/user.service';
import { SearchMarketService } from '../../../../../shared/services/search-market.service';
import { TableModule } from 'primeng/table';
import { FloatLabelModule } from 'primeng/floatlabel';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { HeadingComponent } from '../../../../../shared/components/heading/heading.component';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { InputTextModule } from 'primeng/inputtext';
import { CommonModule } from '@angular/common';
import { CheckboxModule } from 'primeng/checkbox';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
 DynamicDialogModule } from 'primeng/dynamicdialog';
import { ConfirmationDialogService } from '../../../../../shared/services/confirmation-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { CreateSearchMarketModalComponent } from './modals/create/create-search-market.component';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { getSessionFirm } from '../../../../../store/user/user.selector';
import { SessionUser } from '../../../../../store/user/user.interface';
import { Store } from '@ngrx/store';
import { HelperService } from '../../../../../shared/services/helper.service';
import { PipesModule } from '../../../../../shared/pipes/pipes.module';

@Component({
  selector: 'app-search-markets',
  standalone: true,
  imports: [
    CheckboxModule,
    FloatLabelModule,
    DialogModule,
    ButtonModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    HeadingComponent,
    InputTextModule,
    TableModule,
    CommonModule,
    ReactiveFormsModule,
    DynamicDialogModule,
    CreateSearchMarketModalComponent,
    TooltipModule,
    TagModule,
    PipesModule
  ],
  templateUrl: './search-markets.component.html',
  styleUrl: './search-markets.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [DialogService, DynamicDialogRef, DynamicDialogConfig],
})
export class SearchMarketComponent implements OnInit {
  searchMarkets: any;
  createForm!: FormGroup;
  ref: DynamicDialogRef | undefined;
  showEditCreate: boolean = false;

  /**
   * MP-5638/MP-5641: may this user ADMINISTER taxonomy?
   *
   * Taxonomy administration is FM-only once a firm is part of a family. The
   * firm's own settings flag still decides whether the feature exists at all;
   * this is ANDed with it in the template so the create, edit, archive and
   * delete controls are hidden from a user the server would refuse, rather
   * than letting them submit into a 403.
   *
   * See `UserService.canManageTaxonomy()` for the rule and its one known gap.
   */
  readonly canManageTaxonomy = signal(false);

  user: any;
  firm: any;
  row: any;
  constructor(
    private userService: UserService,
    private searchMarketService: SearchMarketService,
    public dialogService: DialogService,
    private store: Store<SessionUser>,
    private confirmationDialogService: ConfirmationDialogService,
    private toastService: ToastService,
    public config: DynamicDialogConfig,
    public helperService: HelperService
  ) {}

  ngOnInit() {
    this.user = this.userService.getSessionUser();
    this.canManageTaxonomy.set(this.userService.canManageTaxonomy());
    this.getFirm();
    this.getFirmSearchMarkets();
  }

  getFirm(){
    this.store.select(getSessionFirm).subscribe((res) => {
      this.firm = res;
      this.showEditCreate = this.firm.isDynamicFirmSettingsEnabled || this.firm.isSingleSearchMarketEnabled;
    });
  }

  getFirmSearchMarkets() {
    this.searchMarketService
      .getFirmSearchMarkets(this.user.firm._id)
      .subscribe({
        next: (searchMarkets) => {
          this.searchMarkets = searchMarkets;
        },
      });
  }

  onArchive($event: Event, searchMarket: any) {
    $event.stopPropagation();
    this.confirmationDialogService.confirm({
      target: $event.target as EventTarget,
      message:
        'You are about to ' + (searchMarket.isActive ? 'deactivate ' : 'activate ') + searchMarket.name + '.',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: async () => {
        const { associations } = await this.searchMarketService.getSMAssociations(searchMarket?._id);
        if ( !Object.keys(associations).length) {
          searchMarket.isActive = !searchMarket.isActive;
          this.searchMarketService
            .updateSearchMarket(searchMarket._id, searchMarket)
            .subscribe({
              next: (searchMarkets) => {
                this.getFirmSearchMarkets();
              },
            });
        } else {
          this.showSearchMarketModal(associations, $event);
        }
      },
    });
  }

  onDelete($event: Event, searchMarket: any) {
    $event.stopPropagation();
    this.confirmationDialogService.confirm({
      target: $event.target as EventTarget,
      message: 'Are you sure you want to delete ' + searchMarket?.name + '?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: async () => {
        const res = await this.searchMarketService.getSMAssociations(searchMarket?._id);
        if (res.associations && Object.keys(res.associations).length) {
          this.showSearchMarketModal(res.associations, $event);
        }
        else if (res.reviewersCount > 0) {
          this.showReviewersConfirmationModal(searchMarket, res.reviewersCount, $event);
        }
        else {
          this.deleteSearchMarket(searchMarket._id);
        }
      },
    });
  }

  showCreateSearchMarketModal($event: Event, searchMarket?: any) {
    $event.stopPropagation();
    this.ref = this.dialogService.open(CreateSearchMarketModalComponent, {
      header: 'Create',
      width: '30vw',
      contentStyle: { 'overflow': 'hidden' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      data: {
        buttonText: 'Create'
      }
    });

    this.ref.onClose.subscribe((data) => {
      if (data) {
        this.searchMarketService
          .createSearchMarket({
            ...data,
            isApproved: true,
            firm: this.user.firm._id,
          })
          .subscribe({
            next: (res) => {
              this.getFirmSearchMarkets();
              this.toastService.showSuccess(' Created successfully.');
            },
          });
      }
    });
  }

  showEditSearchMarketModal($event: Event, searchMarket?: any) {
    $event.stopPropagation();
    this.ref = this.dialogService.open(CreateSearchMarketModalComponent, {
      header: 'Edit',
      width: '30vw',
      contentStyle: { 'overflow': 'hidden' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      
      data: {
        ...searchMarket,
        buttonText: 'Edit'
      }
    });

    this.ref.onClose.subscribe(async (data) => {
      if (data) {

        const { associations } = await this.searchMarketService.getSMAssociations(searchMarket?._id);
        if ( !Object.keys(associations).length) {
          this.searchMarketService
            .updateSearchMarket(searchMarket._id,{
              ...data,
              isApproved: true,
              firm: this.user.firm._id,
            })
            .subscribe({
              next: (res) => {
                this.getFirmSearchMarkets();
                this.toastService.showSuccess(' Updated successfully.');
              },
            });
        } else {
          this.showSearchMarketModal(associations, $event);
        }
      }
    });
  }

  showSearchMarketModal(res: any, $event: Event): void{
    const { roles = { count: 0, values: [] } } = res;
  
    const rolesText = this.helperService.formatList("Roles", roles);

    const message = [rolesText].filter(Boolean).join("<br>");

    this.confirmationDialogService.confirm({
      target: $event.target as EventTarget,
      message:`The search market is associated with the following:<br><br>${message}`,
      header: 'Cannot Delete/Deactivate Search Market',
      icon: 'none',
      acceptIcon: 'none',
      rejectVisible: false,
      acceptLabel: 'OK',
      accept: () => {}
    })
  }

  showReviewersConfirmationModal(searchMarket: any, reviewersCount: number, $event: Event): void {
    this.confirmationDialogService.confirm({
      target: $event.target as EventTarget,
      message: `There are currently ${reviewersCount > 1 ? reviewersCount + ' candidates' : '1 candidate'} with this search market in their profile.<br> ${searchMarket?.name} will be removed from their profile. Are you sure you want to continue?`,
      header: 'Delete Search Market',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: () => {
        this.deleteSearchMarket(searchMarket?._id);
      }
    });
  }

  deleteSearchMarket(searchMarketId: string): void {
    this.searchMarketService
      .deleteSearchMarket(searchMarketId)
      .subscribe({
        next: (searchMarkets) => {
          this.getFirmSearchMarkets();
          this.toastService.showSuccess('Search market deleted successfully.');
        },
        error: (error) => {
          this.toastService.showError('Failed to delete search market.');
        }
      });
  }
}
